import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { Badge } from '@reflex/ui/components/ui/badge';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { buildSwitchTarget } from './buildSwitchTarget';

/**
 * Organization picker for nav chrome. Deliberately not a `PanelTitle` — the
 * dock header and the agent session title used to share the same type ramp,
 * so both top bars read as twin titles. This stays a compact control label.
 */
export function OrgSwitcher() {
  const { memberships, activeOrganization, activeOrganizationId } = useOrganization();
  const { canCreateOrganization } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!memberships || memberships.length === 0) return null;

  const knownSlugs = new Set(memberships.map((m) => m.organization.slug));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex w-full min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-left transition-colors duration-150 hover:bg-sidebar-accent/40 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/60 data-[state=open]:bg-sidebar-accent/40"
          data-testid="org-switcher-trigger"
          aria-label="Switch organization"
        >
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground/55" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
            {activeOrganization?.name ?? 'Select organization'}
          </span>
          {activeOrganization?.subscription?.type === 'trial' && (
            <Badge
              variant="secondary"
              className="shrink-0 px-1.5 py-0 text-2xs font-medium uppercase tracking-wide"
              data-testid="org-switcher-trial-badge"
            >
              Trial
            </Badge>
          )}
          <ChevronsUpDown
            className="h-3 w-3 shrink-0 text-muted-foreground/45 transition-colors group-hover:text-muted-foreground/70"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
        align="start"
        sideOffset={6}
      >
        <DropdownMenuLabel className="-mx-1 -mt-1 mb-1 border-b border-border bg-muted/60 px-2.5 py-1.5 text-2xs font-medium uppercase tracking-wider text-muted-foreground">
          {memberships.length > 1 ? 'Switch organization' : 'Organization'}
        </DropdownMenuLabel>
        {memberships.map(({ organization }) => {
          const active = organization.id === activeOrganizationId;
          return (
            <DropdownMenuItem
              key={organization.id}
              onSelect={() =>
                navigate(buildSwitchTarget(location.pathname, knownSlugs, organization.slug))
              }
              className="gap-2"
              data-testid={`org-switcher-item-${organization.slug}`}
              data-active={active ? 'true' : undefined}
            >
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-sm">{organization.name}</span>
              {active && (
                <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        {canCreateOrganization ? (
          <DropdownMenuItem
            onSelect={() => navigate('/org-setup/new')}
            className="gap-2"
            data-testid="org-switcher-create-org"
            aria-label="Create organization"
          >
            <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-sm">Create organization</span>
          </DropdownMenuItem>
        ) : (
          // Self-serve creation is off platform-wide. Say why rather than
          // offering an action the server would reject with a 403.
          <p
            className="px-2 py-1.5 text-xs text-muted-foreground"
            data-testid="org-switcher-create-org-disabled"
          >
            An administrator has turned off self-serve organization creation.
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
